import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from '@opendate-app/setlist';
import client from '../../../shared/api/client';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import { currencyFormatter } from '../utils/reportingUtils';

const PER_PAGE = 25;
const EMPTY_PAGINATION = { current_page: 1, total_pages: 1, total_entries: 0, per_page: PER_PAGE };

const formatDate = (value) => (value
  ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  : '—');

const titleize = (value) => (value ? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—');

function ReportingPledgesTable({ team, endpoint, drilldownId, startDate, endDate }) {
  const queryKey = JSON.stringify([team && team.id, endpoint, drilldownId, startDate, endDate]);
  const [pageState, setPageState] = useState({ queryKey, page: 1 });
  const page = pageState.queryKey === queryKey ? pageState.page : 1;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pledges, setPledges] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  useEffect(() => {
    if (!team || !team.id) return undefined;
    let active = true;
    setLoading(true);
    setError(null);

    const params = { drilldown_id: drilldownId, page, per_page: PER_PAGE };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    client.get(endpoint, { params })
      .then((res) => {
        if (!active) return;
        setPledges(res.data.pledges || []);
        setPagination(res.data.pagination || EMPTY_PAGINATION);
      })
      .catch(() => { if (active) setError('Failed to load pledges.'); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [team && team.id, endpoint, drilldownId, startDate, endDate, page]);

  const total = pagination.total_entries || 0;
  const canCrm = team && team.package && team.package.crm;

  const columns = [
    { key: 'pledge_number', header: 'Pledge #', render: (row) => row.pledge_number },
    {
      key: 'donor_name',
      header: 'Donor',
      primary: true,
      render: (row) => (canCrm && row.fan_id ? (
        <a href={`/teams/${team.id}/crm/fans/${row.fan_id}`}>{row.donor_name || '(No name)'}</a>
      ) : (row.donor_name || '(No name)')),
    },
    { key: 'status', header: 'Status', render: (row) => titleize(row.status) },
    { key: 'pledge_date', header: 'Pledge Date', render: (row) => formatDate(row.pledge_date) },
    { key: 'total_amount', header: 'Pledged', render: (row) => currencyFormatter.format(row.total_amount || 0) },
    { key: 'received_amount', header: 'Received', render: (row) => currencyFormatter.format(row.received_amount || 0) },
    { key: 'outstanding_amount', header: 'Outstanding', render: (row) => currencyFormatter.format(row.outstanding_amount || 0) },
  ];

  return (
    <div className="mt-4" style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8 }}>
      <div className="px-4 pt-3" style={{ fontSize: 16, fontWeight: 600, color: 'var(--sl-text-primary, #101828)' }}>Pledges</div>
      <div className="px-4 py-2 text-muted" aria-live="polite">
        {loading ? 'Loading…' : `${total.toLocaleString()} pledge${total !== 1 ? 's' : ''}`}
      </div>
      {error && <div className="alert alert-danger mx-4">{error}</div>}
      <Table
        columns={columns}
        data={loading || error ? [] : pledges}
        getRowKey={(row) => row.id}
        empty={loading ? 'Loading…' : error ? 'Pledges could not be loaded.' : 'No pledges on this campaign.'}
      />
      {pagination.total_pages > 1 && (
        <div style={{ padding: '12px 24px', borderTop: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
          <TeamPagination
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            totalItems={total}
            pageSize={pagination.per_page || PER_PAGE}
            itemLabel="pledges"
            onPageChange={(p) => { if (!loading && p >= 1 && p <= pagination.total_pages) setPageState({ queryKey, page: p }); }}
          />
        </div>
      )}
    </div>
  );
}

ReportingPledgesTable.propTypes = {
  team: PropTypes.object.isRequired,
  endpoint: PropTypes.string.isRequired,
  drilldownId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

export default ReportingPledgesTable;
